import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useT, Bi } from "@/lib/i18n";
import { Wordmark } from "@/components/Brand";
import { Field, BigInput, Chips, Stepper } from "@/components/form";
import { AddRoomsForm } from "@/components/AddRoomsForm";

export function PropertyForm({ onSaved, initial }: { onSaved: (p: any) => void; initial?: any }) {
  const { t } = useT();
  const [f, setF] = useState({ name: initial?.name || "", address: initial?.address || "", rent_due_day: initial?.rent_due_day || 5, proration_mode: initial?.proration_mode || "prorated" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const r = initial ? await api.patch(`/properties/${initial.id}`, f) : await api.post("/properties", f);
      onSaved(r.data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-4">
      <Field label={<Bi k="property_name" />}><BigInput data-testid="property-name-input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="e.g. Shanti PG for Men" /></Field>
      <Field label={t("address")}><BigInput data-testid="property-address-input" value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} /></Field>
      <Field label={t("rent_due_day")}><Stepper testId="property-due-day-stepper" value={f.rent_due_day} min={1} max={28} onChange={(v) => setF({ ...f, rent_due_day: v })} /></Field>
      <Field label={<Bi k="proration" />} hint={t("proration_help")}>
        <Chips testId="property-proration-chips" value={f.proration_mode} onChange={(v) => setF({ ...f, proration_mode: v })}
          options={[{ value: "prorated", label: t("proration_prorated") }, { value: "full", label: t("proration_full") }]} />
      </Field>
      <Button data-testid="property-save-btn" disabled={!f.name.trim() || busy} onClick={save} className="w-full h-14 rounded-xl text-base bg-emerald-700 hover:bg-emerald-800">
        {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : t(initial ? "save" : "next")}
      </Button>
    </div>
  );
}

export default function Onboarding() {
  const { t } = useT();
  const nav = useNavigate();
  const { reloadProperties, selectProperty, owner, setOwner } = useAuth();
  const [step, setStep] = useState(0);
  const [prop, setProp] = useState<any>(null);
  const steps = [t("ob_step_property"), t("ob_step_rooms"), t("ob_step_done")];
  const finish = async () => {
    const r = await api.patch("/auth/me", { onboarded: true });
    setOwner({ ...owner, ...r.data });
    await reloadProperties();
    selectProperty(prop.id);
    nav("/");
  };
  return (
    <div className="min-h-screen bg-[#F6F7F5]">
      <div className="max-w-md mx-auto px-5 pt-6 pb-16">
        <Wordmark />
        <h1 className="mt-8 text-3xl font-extrabold tracking-tight"><Bi k="ob_title" /></h1>
        <div className="mt-5 flex gap-2" data-testid="onboarding-steps">
          {steps.map((s, i) => (
            <div key={s} className="flex-1">
              <div className={`h-1.5 rounded-full ${i <= step ? "bg-emerald-600" : "bg-slate-200"}`} />
              <p className={`mt-1.5 text-xs font-semibold ${i === step ? "text-emerald-800" : "text-slate-400"}`}>{i + 1}. {s}</p>
            </div>
          ))}
        </div>
        <div className="mt-6 rounded-3xl bg-white border border-slate-200/80 p-5 shadow-sm pg-rise" key={step}>
          {step === 0 && <PropertyForm onSaved={(p) => { setProp(p); setStep(1); }} />}
          {step === 1 && <AddRoomsForm propertyId={prop.id} onDone={() => setStep(2)} />}
          {step === 2 && (
            <div className="text-center py-6" data-testid="onboarding-done">
              <CheckCircle2 className="h-14 w-14 text-emerald-600 mx-auto" />
              <h2 className="mt-4 text-xl font-bold">{t("ob_done_title")}</h2>
              <p className="mt-2 text-sm text-slate-500">{t("ob_done_text")}</p>
              <Button data-testid="onboarding-finish-btn" onClick={finish} className="mt-6 w-full h-14 rounded-xl text-base bg-emerald-700 hover:bg-emerald-800">{t("go_dashboard")}</Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
